/**
 * VideoAnnotationToolbar Component
 *
 * Toolbar for selecting annotation tools, colors, and stroke settings.
 */

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import {
  Pencil,
  Highlighter,
  ArrowRight,
  Circle,
  Square,
  Minus,
  Type,
  Eraser,
  Undo2,
  Redo2,
  Trash2,
  Download,
  Palette,
} from "lucide-react";
import type { AnnotationTool } from "./VideoAnnotationCanvas";

interface VideoAnnotationToolbarProps {
  currentTool: AnnotationTool;
  onToolChange: (tool: AnnotationTool) => void;
  currentColor: string;
  onColorChange: (color: string) => void;
  strokeWidth: number;
  onStrokeWidthChange: (width: number) => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onClear?: () => void;
  onExport?: () => void;
  className?: string;
  compact?: boolean;
}

const ANNOTATION_TOOLS: {
  value: AnnotationTool;
  label: string;
  icon: React.ReactNode;
  shortcut?: string;
}[] = [
  { value: "pen", label: "Pen", icon: <Pencil className="h-4 w-4" />, shortcut: "P" },
  {
    value: "highlighter",
    label: "Highlighter",
    icon: <Highlighter className="h-4 w-4" />,
    shortcut: "H",
  },
  { value: "arrow", label: "Arrow", icon: <ArrowRight className="h-4 w-4" />, shortcut: "A" },
  { value: "circle", label: "Circle", icon: <Circle className="h-4 w-4" />, shortcut: "C" },
  { value: "rectangle", label: "Rectangle", icon: <Square className="h-4 w-4" />, shortcut: "R" },
  { value: "line", label: "Line", icon: <Minus className="h-4 w-4" />, shortcut: "L" },
  { value: "text", label: "Text", icon: <Type className="h-4 w-4" />, shortcut: "T" },
  { value: "eraser", label: "Eraser", icon: <Eraser className="h-4 w-4" />, shortcut: "E" },
];

const PRESET_COLORS = [
  "#ef4444", // Red
  "#f97316", // Orange
  "#eab308", // Yellow
  "#22c55e", // Green
  "#3b82f6", // Blue
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#ffffff", // White
  "#000000", // Black
];

export function VideoAnnotationToolbar({
  currentTool,
  onToolChange,
  currentColor,
  onColorChange,
  strokeWidth,
  onStrokeWidthChange,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  onClear,
  onExport,
  className,
  compact = false,
}: VideoAnnotationToolbarProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 p-2 bg-background border rounded-lg shadow-sm",
        compact ? "flex-wrap" : "",
        className
      )}
    >
      {/* Drawing Tools */}
      <ToggleGroup
        type="single"
        value={currentTool}
        onValueChange={(value) => value && onToolChange(value as AnnotationTool)}
        className={cn("gap-1", compact && "flex-wrap")}
      >
        {ANNOTATION_TOOLS.map((tool) => (
          <Tooltip key={tool.value}>
            <TooltipTrigger asChild>
              <ToggleGroupItem
                value={tool.value}
                aria-label={tool.label}
                className={cn(
                  "h-8 w-8",
                  currentTool === tool.value && "bg-primary text-primary-foreground"
                )}
              >
                {tool.icon}
              </ToggleGroupItem>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {tool.label}
                {tool.shortcut && (
                  <span className="ml-2 text-xs opacity-70">({tool.shortcut})</span>
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
      </ToggleGroup>

      <Separator orientation="vertical" className="h-6" />

      {/* Color Picker */}
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8">
                <div
                  className="h-5 w-5 rounded-full border"
                  style={{ backgroundColor: currentColor }}
                />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Color</TooltipContent>
        </Tooltip>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="space-y-3">
            <p className="text-sm font-medium">Color</p>
            <div className="grid grid-cols-5 gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  className={cn(
                    "h-7 w-7 rounded-full border-2 transition-transform hover:scale-110",
                    currentColor === color
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-transparent"
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => onColorChange(color)}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-muted-foreground" />
              <input
                type="color"
                value={currentColor}
                onChange={(e) => onColorChange(e.target.value)}
                className="h-7 w-20 cursor-pointer rounded border"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Stroke Width */}
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-14 px-2">
                <div className="flex items-center gap-1">
                  <div
                    className="rounded-full bg-foreground"
                    style={{
                      width: Math.min(strokeWidth * 2, 12),
                      height: Math.min(strokeWidth * 2, 12),
                    }}
                  />
                  <span className="text-xs">{strokeWidth}</span>
                </div>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Stroke Width</TooltipContent>
        </Tooltip>
        <PopoverContent className="w-48" align="start">
          <div className="space-y-3">
            <p className="text-sm font-medium">Stroke Width: {strokeWidth}px</p>
            <Slider
              value={[strokeWidth]}
              onValueChange={([value]) => onStrokeWidthChange(value)}
              min={1}
              max={20}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between">
              {[1, 3, 5, 10, 15].map((width) => (
                <button
                  key={width}
                  className={cn(
                    "h-8 w-8 rounded border flex items-center justify-center transition-colors",
                    strokeWidth === width
                      ? "border-primary bg-primary/10"
                      : "hover:bg-muted"
                  )}
                  onClick={() => onStrokeWidthChange(width)}
                >
                  <div
                    className="rounded-full bg-foreground"
                    style={{
                      width: Math.min(width * 1.5, 16),
                      height: Math.min(width * 1.5, 16),
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6" />

      {/* Undo/Redo */}
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={!canUndo}
              onClick={onUndo}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Undo <span className="text-xs opacity-70">(Ctrl+Z)</span>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={!canRedo}
              onClick={onRedo}
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Redo <span className="text-xs opacity-70">(Ctrl+Y)</span>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Clear */}
      {onClear && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={onClear}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Clear All</TooltipContent>
        </Tooltip>
      )}

      {/* Export */}
      {onExport && (
        <>
          <Separator orientation="vertical" className="h-6" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={onExport}
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Export Image</TooltipContent>
          </Tooltip>
        </>
      )}
    </div>
  );
}

export default VideoAnnotationToolbar;
